import { Link } from '@remix-run/react';
import {
    Flex,
    TileGrid,
    Button,
    useStyles,
    useAppDisplayModeContext,
    pxToRem,
} from '@archipro-design/aria';
import * as S from './ProductRange.style';
import ProductTile from '@modules/tile/component/product-tile/ProductTile';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import { useTracker } from '@archipro-website/tracker';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useTileHotButtons } from '~/modules/root';

interface ProductRangeData {
    Professional: Pick<
        ProductPageLoaderData['product']['Professional'],
        'Link' | 'URLSegment' | 'ID' | 'Title'
    >;
    RangeTitle: string;
    ProductRange: ProductPageLoaderData['product']['ProductRange'];
}
export interface ProductRangeProps {
    data: ProductRangeData;
}

const TILES_DISPLAYED_DESKTOP = 10;
const TILES_DISPLAYED_MOBILE = 4;

const getProductRangeUrl = (
    professional: ProductRangeData['Professional'],
    rangeName: string
) => {
    const professionalProducts = `${professional.Link}/our-products`;

    // Note: Filtering is currently done using url search params which may change
    // to path params in the future.
    const searchParams = new URLSearchParams();
    if (rangeName) {
        searchParams.set('productRanges', rangeName);
    }
    if (professional.URLSegment) {
        searchParams.set('professionals', professional.URLSegment);
    }
    const constructRangeParams = searchParams.toString();

    return constructRangeParams
        ? `${professionalProducts}?${constructRangeParams}#mainContent`
        : professionalProducts;
};

const FeaturedProductRange = ({
    data: { ProductRange, Professional, RangeTitle },
}: ProductRangeProps) => {
    const { css } = useStyles();
    const appDisplayMode = useAppDisplayModeContext();
    const { mobile } = appDisplayMode;
    const { favouriteItem } = useSaveToDesignBoard('ProductPin');
    const tracker = useTracker();
    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const productIDs = ProductRange?.edges?.map((p) => p.node.ID as number);

    const { getTileTopActions, getTileAdminCaption, hotButtonModal } =
        useTileHotButtons({
            itemType: 'product',
            itemIDs: productIDs,
        });

    if (!ProductRange || !ProductRange.edges?.length) {
        return null;
    }

    const { pageInfo, edges } = ProductRange;

    const totalCount = pageInfo?.totalCount;
    const tilesDisplayed = mobile
        ? TILES_DISPLAYED_MOBILE
        : TILES_DISPLAYED_DESKTOP;
    const displayLoadMore = totalCount > tilesDisplayed;

    const libraryRangeUrl = getProductRangeUrl(Professional, RangeTitle);

    return (
        <div className={css(S.ProductRangeWrapper)}>
            <TileGrid
                heading={{
                    children: (
                        <>
                            Other products
                            <br />
                            in this range
                        </>
                    ),
                    variables: {
                        fontWeight: 500,
                    },
                }}
                data-testid="product-range-grid"
                className={css(S.ProductRangeGrid)}
                columns={mobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)'}
            >
                {edges.slice(0, tilesDisplayed).map((tile, idx) => {
                    const productId = tile.node.ID as number;

                    const topActions = getTileTopActions({
                        item: tile.node,
                        onDeleteClick: () => willDelete(productId),
                    });

                    const adminCaption = getTileAdminCaption(productId);

                    return (
                        <ProductTile
                            key={`product-range-tile-${idx}`}
                            data={{
                                Product: tile.node,
                                Professional,
                            }}
                            imgLoading="lazy"
                            variant={!mobile ? '01' : '02'}
                            size={!mobile ? 'large' : 'medium'}
                            favouriteItem={favouriteItem}
                            variables={{
                                hoverColor: 'inherit',
                            }}
                            onClick={() => {
                                tracker.log('ProductRangeClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        ExtraData: tile.node.ID,
                                    },
                                });
                            }}
                            topActions={topActions}
                            adminCaption={adminCaption}
                        />
                    );
                })}
            </TileGrid>
            {displayLoadMore && (
                <Flex
                    className={css({
                        margin: mobile ? '0 auto' : `0 0 0 ${pxToRem(930)}`,
                    })}
                >
                    <Button
                        as={Link}
                        prefetch="intent"
                        to={libraryRangeUrl}
                        size={16}
                        className={css(S.ViewRangeButton)}
                        outlined={true}
                    >
                        View more
                    </Button>
                </Flex>
            )}
            {favouriteItem.target && (
                <SaveToDesignBoard {...favouriteItem} autoReposition={false} />
            )}
            {itemIDToDelete && (
                <DeleteItemConfirmModal
                    title={'Delete product'}
                    content={
                        'Are you sure you would like to delete this product? All information and associated tags will be lost.'
                    }
                    open={true}
                    onCancel={cancelDelete}
                    onConfirm={confirmDelete}
                />
            )}
            {hotButtonModal}
        </div>
    );
};

export default FeaturedProductRange;
